export type DiagramKind =
  | 'personagrama-flow'
  | 'cha'
  | 'training-structure'
  | 'formation-structure'
  | 'journey-formation-training'
  | 'mentoring-cycle'
  | 'role-hierarchy';

type SequenceItem = {
  title: string;
  detail?: string;
};

type DiagramProps = {
  kind: DiagramKind;
  caption?: string;
};

const SEQUENCES: Partial<Record<DiagramKind, { label: string; items: SequenceItem[] }>> = {
  'personagrama-flow': {
    label: 'Fluxo de desenvolvimento no Personagrama',
    items: [
      { title: 'Diagnóstico', detail: 'ponto de partida' },
      { title: 'Desenvolvimento', detail: 'prioridades e evolução' },
      { title: 'Plano', detail: 'objetivos e ações' },
      { title: 'Jornada', detail: 'caminho' },
      { title: 'Formação / Treinamento', detail: 'programa e aprendizado' },
      { title: 'Prática', detail: 'aplicação' },
      { title: 'Evidências', detail: 'demonstração' },
      { title: 'Mentoria', detail: 'acompanhamento' },
      { title: 'Evolução', detail: 'novo ciclo' },
    ],
  },
  cha: {
    label: 'Estrutura CHA no Personagrama',
    items: [
      { title: 'Conhecimento', detail: 'saber e compreender' },
      { title: 'Habilidade', detail: 'aplicar na prática' },
      { title: 'Atitude', detail: 'agir e sustentar comportamentos' },
    ],
  },
  'training-structure': {
    label: 'Estrutura de um Treinamento',
    items: [
      { title: 'Treinamento', detail: 'objetivo de aprendizagem' },
      { title: 'Módulos', detail: 'organizam os temas' },
      { title: 'Aulas', detail: 'entregam conteúdo e recursos' },
    ],
  },
  'formation-structure': {
    label: 'Estrutura de uma Formação',
    items: [
      { title: 'Formação', detail: 'programa de desenvolvimento' },
      { title: 'Etapas', detail: 'momentos do percurso' },
      { title: 'Componentes', detail: 'experiências a executar' },
    ],
  },
};

const MENTORING_CYCLE: SequenceItem[] = [
  { title: 'Contexto', detail: 'Visão 360 e momento do mentorado' },
  { title: 'Mentoria', detail: 'reflexão e orientação' },
  { title: 'Acordos', detail: 'próximos passos claros' },
  { title: 'Execução', detail: 'ação do mentorado' },
  { title: 'Evidências', detail: 'resultados e demonstrações' },
  { title: 'Acompanhamento', detail: 'progresso e dificuldades' },
  { title: 'Nova Mentoria', detail: 'o ciclo recomeça' },
];

export function Diagram({ kind, caption }: DiagramProps) {
  if (kind === 'journey-formation-training') {
    return (
      <figure className="help-diagram" aria-label="Diferença entre Jornada, Formação e Treinamento">
        <div className="diagram-comparison" role="list">
          <DiagramCard title="Treinamento" eyebrow="Ensina" text="Organiza aprendizagem em Módulos e Aulas." />
          <DiagramCard title="Formação" eyebrow="Estrutura" text="Organiza um programa em Etapas e Componentes, com regras de execução." />
          <DiagramCard title="Jornada" eyebrow="Orienta o caminho" text="Conecta experiências e mostra onde a pessoa está e o que vem depois." />
        </div>
        <figcaption>{caption ?? 'Os três recursos podem trabalhar juntos no mesmo percurso de desenvolvimento.'}</figcaption>
      </figure>
    );
  }

  if (kind === 'role-hierarchy') {
    return (
      <figure className="help-diagram" aria-label="Hierarquia de papéis do Personagrama">
        <div className="diagram-hierarchy" role="list">
          <div className="diagram-hierarchy-level owner" role="listitem">
            <strong>Owner / Administrador</strong>
            <span>Aluno + Mentor + gestão completa</span>
            <div className="diagram-hierarchy-level mentor">
              <strong>Mentor</strong>
              <span>Aluno + acompanhamento de mentorados vinculados</span>
              <div className="diagram-hierarchy-level student">
                <strong>Aluno / Mentorado</strong>
                <span>Próprio desenvolvimento</span>
              </div>
            </div>
          </div>
        </div>
        <figcaption>{caption ?? 'A lógica é cumulativa: Aluno ⊂ Mentor ⊂ Owner.'}</figcaption>
      </figure>
    );
  }

  if (kind === 'mentoring-cycle') {
    return (
      <figure className="help-diagram" aria-label="Ciclo de acompanhamento contínuo da Mentoria">
        <div className="diagram-cycle" role="list">
          {MENTORING_CYCLE.map((item, index) => (
            <div className="diagram-cycle-step" role="listitem" key={item.title}>
              <span className="diagram-step-number">{index + 1}</span>
              <strong>{item.title}</strong>
              <small>{item.detail}</small>
            </div>
          ))}
        </div>
        <div className="diagram-cycle-return" aria-hidden="true">↺ continuidade</div>
        <figcaption>{caption ?? 'Contexto → Mentoria → Acordos → Execução → Evidências → Acompanhamento → nova Mentoria.'}</figcaption>
      </figure>
    );
  }

  const sequence = SEQUENCES[kind];
  if (!sequence) return null;

  return (
    <figure className="help-diagram" aria-label={sequence.label}>
      <div className="diagram-sequence" role="list">
        {sequence.items.map((item, index) => (
          <div className="diagram-sequence-group" key={item.title}>
            <div className="diagram-node" role="listitem">
              <strong>{item.title}</strong>
              {item.detail ? <small>{item.detail}</small> : null}
            </div>
            {index < sequence.items.length - 1 ? <span className="diagram-arrow" aria-hidden="true">→</span> : null}
          </div>
        ))}
      </div>
      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
}

function DiagramCard({ title, eyebrow, text }: { title: string; eyebrow: string; text: string }) {
  return (
    <div className="diagram-card" role="listitem">
      <span>{eyebrow}</span>
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}
